import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Receipt, PencilLine, Link2, Check, X } from 'lucide-react';
import { ProofList } from './PaymentProofCard';
import { OrderChat } from './OrderChat';
import { EditStatusPill } from './EditOrderCard';
import { money, orderTotals, fmtDate } from '../../lib/orderMoney';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H4 = ({ icon: I, children }) => <h4 className="text-[11px] tracking-widest uppercase text-neutral-500 mb-2 flex items-center gap-2"><I size={13} /> {children}</h4>;

const useBlobUrls = (orderId, proofs, headers) => {
  const [urls, setUrls] = useState({});
  const key = proofs.filter(p => p.content_type).map(p => p.id).join(',');
  useEffect(() => {
    let alive = true; const made = [];
    key.split(',').filter(Boolean).forEach(pid => axios.get(`${API}/admin/orders/${orderId}/proof/${pid}`, { headers, responseType: 'blob' })
      .then(({ data }) => { const u = URL.createObjectURL(data); made.push(u); if (alive) setUrls(x => ({ ...x, [pid]: u })); }).catch(() => {}));
    return () => { alive = false; made.forEach(URL.revokeObjectURL); };
  }, [orderId, key, headers]);
  return urls;
};

const EditReview = ({ order, req, headers, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const decide = async (action) => {
    setBusy(action); setErr('');
    try { await axios.post(`${API}/admin/orders/${order.id}/edit-requests/${req.id}`, { action, note }, { headers }); onDone(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };
  const before = Object.fromEntries((order.items || []).map(i => [String(i.id), i.qty]));
  return (
    <div data-testid={`admin-edit-request-${req.id}`} className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/20 p-3 space-y-2 text-[12.5px]">
      <div className="font-semibold text-amber-800 dark:text-amber-300">Customer asked to change this order · {fmtDate(req.created_at)}</div>
      {req.items.map(i => {
        const was = before[String(i.id)];
        return <div key={i.id}>{i.qty} × {i.name} {was === undefined ? <span className="text-emerald-600 font-semibold">(new)</span> : was !== i.qty ? <span className="text-neutral-500">(was {was})</span> : null}</div>;
      })}
      {(order.items || []).filter(i => !req.items.some(r => String(r.id) === String(i.id))).map(i => <div key={i.id} className="line-through text-red-600">{i.qty} × {i.name}</div>)}
      <div>Total: <span className="line-through text-neutral-500">{money(order, orderTotals(order).total)}</span> → <strong data-testid="admin-edit-new-total">{money(order, orderTotals(order, req).total)}</strong>{order.currency !== 'USD' && order.local_totals ? ` (≈ $${Number(req.total).toFixed(2)})` : ''}</div>
      {req.note && <div className="italic text-neutral-600 dark:text-neutral-400">“{req.note}”</div>}
      <input data-testid="admin-edit-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to customer (optional)" className="w-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md px-2.5 py-1.5" />
      <div className="flex gap-2">
        <button data-testid="admin-edit-approve" disabled={!!busy} onClick={() => decide('approve')} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-3 py-1.5 font-semibold disabled:opacity-50"><Check size={13} /> {busy === 'approve' ? '…' : 'Approve'}</button>
        <button data-testid="admin-edit-reject" disabled={!!busy} onClick={() => decide('reject')} className="inline-flex items-center gap-1 rounded-md border border-neutral-300 dark:border-neutral-700 px-3 py-1.5 disabled:opacity-50"><X size={13} /> {busy === 'reject' ? '…' : 'Decline'}</button>
      </div>
      {err && <div className="text-red-600">{err}</div>}
    </div>
  );
};

export const AdminOrderPortal = ({ order, headers, onOrderChange }) => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const load = useCallback(() => axios.get(`${API}/admin/orders/${order.id}/portal`, { headers }).then(({ data: d }) => {
    setData(d); onOrderChange(d.order);
  }).catch(() => {}), [order.id, headers]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); const h = setInterval(load, 20000); return () => clearInterval(h); }, [load]);
  const urls = useBlobUrls(order.id, data?.proofs || [], headers);
  if (!data) return <div className="text-[12.5px] text-neutral-500">Loading payment proof & messages…</div>;
  const pending = data.edit_requests.find(r => r.status === 'pending');
  const send = async (text) => { await axios.post(`${API}/admin/orders/${order.id}/messages`, { text }, { headers }); await load(); };
  const link = data.customer_link || (data.order.access_token ? `${window.location.origin}/order/${order.id}?t=${data.order.access_token}` : '');
  const copy = () => { navigator.clipboard?.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div data-testid={`admin-order-portal-${order.id}`} className="space-y-4">
      <div>
        <H4 icon={Receipt}>Payment proof ({data.proofs.length})</H4>
        {data.proofs.length ? <ProofList proofs={data.proofs} srcFor={(p) => urls[p.id] || ''} testPrefix="admin-proof" />
          : <div className="text-[12.5px] text-neutral-500">The customer hasn't uploaded a screenshot or transaction ID yet.</div>}
      </div>
      <div>
        <H4 icon={PencilLine}>Change requests</H4>
        {pending ? <EditReview order={data.order} req={pending} headers={headers} onDone={load} /> : <div className="text-[12.5px] text-neutral-500">No pending change request.</div>}
        {data.edit_requests.filter(r => r.status !== 'pending').slice(0, 3).map(r => (
          <div key={r.id} className="mt-1.5 text-[11.5px] text-neutral-500 flex items-center gap-2"><EditStatusPill status={r.status} /> {fmtDate(r.created_at)} · {money(data.order, orderTotals(data.order, r).total)}</div>
        ))}
      </div>
      <OrderChat messages={data.messages} onSend={send} me="admin" title="Chat with customer" hint="Customer is emailed on each message" testPrefix={`admin-chat-${order.id}`} />
      {link && (
        <button data-testid={`copy-customer-link-${order.id}`} onClick={copy} className="inline-flex items-center gap-1.5 text-[12px] text-neutral-600 dark:text-neutral-300 hover:text-brand">
          {copied ? <Check size={12} /> : <Link2 size={12} />} {copied ? 'Copied' : "Copy customer's private order link"}
        </button>
      )}
    </div>
  );
};
